// Image-edit API — healing/clone tool och framtida edits.
//
// Originalet modifieras aldrig. Varje edit blir en ny fil-version på
// servern, och listas via GET /api/images/:id/edits.

import { request } from './client';

export interface EditUser {
  id: number;
  name: 'henrik' | 'michelle';
  displayName?: string;
}

export interface ImageEdit {
  id: string;
  imageId: string;
  type: 'heal' | string;
  params?: unknown;
  width?: number | null;
  height?: number | null;
  fileSizeBytes?: number | null;
  createdAt: string;
  user?: EditUser | null;
  /** Färdig URL till denna edit. Frontend kan annars bygga den från id:n. */
  url?: string;
}

interface ListEditsResponse {
  edits: ImageEdit[];
}

interface CreateEditResponse {
  edit: ImageEdit;
}

export async function listEdits(imageId: string): Promise<ImageEdit[]> {
  const data = await request<ListEditsResponse>(`/images/${imageId}/edits`);
  return data.edits;
}

/**
 * Posta en healad bild till backend. `blob` ska vara en JPEG (typiskt från
 * canvas.toBlob('image/jpeg', 0.92)). Backend re-encodar den ändå för
 * konsistent kvalitet.
 *
 * `params` är frivilligt JSON-objekt med metadata (mask coords, brush size,
 * algorithm-val) som lagras i SQLite för framtida re-rendering på full
 * upplösning.
 */
export async function createEdit(
  imageId: string,
  blob: Blob,
  opts: { type?: string; params?: unknown } = {},
): Promise<ImageEdit> {
  const headers: Record<string, string> = {
    'Content-Type': 'image/jpeg',
  };
  if (opts.type) headers['X-Loupe-Edit-Type'] = opts.type;
  if (opts.params != null) {
    headers['X-Loupe-Edit-Params'] = JSON.stringify(opts.params);
  }

  // Vi använder fetch direkt eftersom api/client.ts wrappar till JSON.
  const res = await fetch(`/api/images/${imageId}/edits`, {
    method: 'POST',
    headers,
    body: blob,
    credentials: 'include',
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const data = (await res.json()) as CreateEditResponse;
  return data.edit;
}

export async function deleteEdit(imageId: string, editId: string): Promise<void> {
  await request<void>(`/images/${imageId}/edits/${editId}`, { method: 'DELETE' });
}

/**
 * Bygger URL för att hämta en specifik bild-version. `edit` kan vara:
 *  - undefined / 'original' → originalet
 *  - 'latest' → senaste edit (fallback original)
 *  - en specifik edit-id (ULID)
 */
export function editedImageUrl(
  imageId: string,
  size: 'thumb' | 'preview' | 'full' = 'preview',
  edit?: string | null,
): string {
  const params = new URLSearchParams({ size });
  if (edit) params.set('edit', edit);
  return `/api/images/${imageId}/file?${params.toString()}`;
}
